import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { BarChart3, Plus, RefreshCw, Search } from "lucide-react";
import { PortalActivityTemplateSummary } from "../api/contracts";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { invalidateClinicianPortalReferenceData } from "../api/portalRequestCache";
import {
  ActionsColumnHeader,
  ActivityTemplateStatusPill,
  ActivityTypePill,
  PortalDataTable,
  PortalTableColumn,
} from "../components/DataDisplay";
import { ActivityStepsPreview } from "../components/ActivityStepsPreview";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import { RowActionMenu } from "../components/HomeworkControls";
import {
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { PortalLink } from "../components/PortalLink";
import { SingleSelectPillField } from "../components/SingleSelectPillField";
import { templateStepsEditorPath } from "../config/activityStepsNavigation";
import {
  addActivityTemplatePath,
  activityTemplateDetailPath,
  activityTemplateEditPath,
  activityTemplateOverviewPath,
} from "../config/navigation";
import { getActivityTypePillToken } from "../config/palette";
import {
  currentActivityTemplatesListPath,
  withActivityTemplatesReturnTo,
} from "../config/activityTemplateNavigation";
import { usePortalData } from "../hooks/usePortalData";
import {
  CLINICIAN_PORTAL_NAVIGATION_EVENT,
  navigateWithinPortal,
  replaceWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";
import { formatLocalDateTime } from "../utils/dateTime";

type ActivityTemplateStatusFilter = "on" | "off" | "all";
type ActivityTemplateSortKey =
  | "name"
  | "activityType"
  | "status"
  | "activitySteps"
  | "updatedAt";
type ActivityTemplateSortDirection = "ascending" | "descending";

const statusFilterOptions: readonly {
  value: ActivityTemplateStatusFilter;
  label: string;
}[] = [
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
  { value: "all", label: "All" },
];

const EMPTY_TEMPLATES: readonly PortalActivityTemplateSummary[] = [];
const DEFAULT_SORT_KEY: ActivityTemplateSortKey = "name";

function normalizedSortKey(value: string | null): ActivityTemplateSortKey {
  return value === "activityType" ||
    value === "status" ||
    value === "activitySteps" ||
    value === "updatedAt" ||
    value === "name"
    ? value
    : DEFAULT_SORT_KEY;
}

function activityTemplateUpdatedAtValue(
  template: PortalActivityTemplateSummary,
): number | null {
  if (!template.updatedAt) return null;
  const timestamp = Date.parse(template.updatedAt);
  return Number.isNaN(timestamp) ? null : timestamp;
}

function compareActivityTemplateUpdatedAt(
  left: PortalActivityTemplateSummary,
  right: PortalActivityTemplateSummary,
  direction: ActivityTemplateSortDirection,
): number {
  const leftTimestamp = activityTemplateUpdatedAtValue(left);
  const rightTimestamp = activityTemplateUpdatedAtValue(right);

  if (leftTimestamp === null || rightTimestamp === null) {
    if (leftTimestamp === null && rightTimestamp === null) return 0;
    return leftTimestamp === null ? 1 : -1;
  }

  const comparison = leftTimestamp - rightTimestamp;
  return direction === "ascending" ? comparison : -comparison;
}

function normalizedSortDirection(
  value: string | null,
): ActivityTemplateSortDirection {
  return value === "desc" ? "descending" : "ascending";
}

function activityTemplateSortValue(
  template: PortalActivityTemplateSummary,
  key: Exclude<ActivityTemplateSortKey, "updatedAt">,
): string {
  return template[key].replace(/\s+/g, " ").trim();
}

function compareActivityTemplateValues(
  left: string,
  right: string,
  direction: ActivityTemplateSortDirection,
): number {
  const leftIsEmpty = left.length === 0;
  const rightIsEmpty = right.length === 0;

  if (leftIsEmpty || rightIsEmpty) {
    if (leftIsEmpty && rightIsEmpty) return 0;
    if (direction === "ascending") return leftIsEmpty ? -1 : 1;
    return leftIsEmpty ? 1 : -1;
  }

  const comparison = left.localeCompare(right, undefined, {
    numeric: true,
    sensitivity: "base",
  });
  return direction === "ascending" ? comparison : -comparison;
}

function subscribeToLocation(onStoreChange: () => void): () => void {
  window.addEventListener("popstate", onStoreChange);
  window.addEventListener(CLINICIAN_PORTAL_NAVIGATION_EVENT, onStoreChange);
  return () => {
    window.removeEventListener("popstate", onStoreChange);
    window.removeEventListener(
      CLINICIAN_PORTAL_NAVIGATION_EVENT,
      onStoreChange,
    );
  };
}

function currentLocationSearch(): string {
  return window.location.search;
}

function normalizedStatusFilter(
  value: string | null,
): ActivityTemplateStatusFilter {
  return value === "off" || value === "all" || value === "on" ? value : "on";
}

function updateListSearch(
  update: (search: URLSearchParams) => void,
  mode: "push" | "replace",
): void {
  const search = new URLSearchParams(window.location.search);
  update(search);
  const query = search.toString();
  const href = `${window.location.pathname}${query ? `?${query}` : ""}`;
  if (mode === "replace") replaceWithinPortal(href);
  else navigateWithinPortal(href);
}

export function ActivityTemplatesPage({
  clinicianCode,
}: {
  clinicianCode: string;
}) {
  const [openActivityStepsId, setOpenActivityStepsId] = useState<string | null>(
    null,
  );
  const locationSearch = useSyncExternalStore(
    subscribeToLocation,
    currentLocationSearch,
    () => "",
  );
  const listSearch = useMemo(
    () => new URLSearchParams(locationSearch),
    [locationSearch],
  );
  const statusParameter = listSearch.get("status");
  const statusFilter = normalizedStatusFilter(
    statusParameter?.toLocaleLowerCase() ?? null,
  );
  const search = listSearch.get("q") ?? "";
  const activityTypeFilter = listSearch.get("activityType")?.trim() ?? "";
  const sortKey = normalizedSortKey(listSearch.get("sort"));
  const sortDirection = normalizedSortDirection(listSearch.get("direction"));
  const listReturnTo = currentActivityTemplatesListPath(locationSearch);

  useEffect(() => {
    if (statusParameter !== null && statusParameter !== statusFilter) {
      updateListSearch(
        (nextSearch) => nextSearch.set("status", "on"),
        "replace",
      );
    }
  }, [statusFilter, statusParameter]);

  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getActivityTemplates(clinicianCode, signal),
    [clinicianCode],
  );
  const state = usePortalData(request);
  const refreshTemplates = state.refresh;
  const refresh = useCallback(() => {
    invalidateClinicianPortalReferenceData("portal.getActivityTemplates");
    refreshTemplates();
  }, [refreshTemplates]);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(data?.found && data.authorized && data.therapist);
  const templates = data?.templates ?? EMPTY_TEMPLATES;
  const activityTypeOptions = useMemo(() => {
    const activityTypes = Array.from(
      new Set(
        templates
          .map((template) => template.activityType.trim())
          .filter(Boolean),
      ),
    ).sort((left, right) => left.localeCompare(right));

    return [
      {
        id: "all-activity-types",
        value: "",
        label: "All Activity Types",
      },
      ...activityTypes.map((activityType) => ({
        id: activityType,
        value: activityType,
        label: activityType,
      })),
    ];
  }, [templates]);

  useEffect(() => {
    if (!available || !activityTypeFilter) return;
    if (
      activityTypeOptions.some((option) => option.value === activityTypeFilter)
    ) {
      return;
    }
    updateListSearch(
      (nextSearch) => nextSearch.delete("activityType"),
      "replace",
    );
  }, [activityTypeFilter, activityTypeOptions, available]);

  const statusCounts = useMemo(
    () => ({
      on: templates.filter(
        (template) => template.status.trim().toLocaleLowerCase() === "on",
      ).length,
      off: templates.filter(
        (template) => template.status.trim().toLocaleLowerCase() === "off",
      ).length,
      all: templates.length,
    }),
    [templates],
  );
  const matchingTemplates = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return templates.filter((template) => {
      const normalizedStatus = template.status.trim().toLocaleLowerCase();
      if (statusFilter !== "all" && normalizedStatus !== statusFilter) {
        return false;
      }
      if (
        activityTypeFilter &&
        template.activityType.trim() !== activityTypeFilter
      ) {
        return false;
      }
      if (!query) return true;
      return [
        template.name,
        template.activityType,
        template.activitySteps,
        template.status,
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [activityTypeFilter, search, statusFilter, templates]);
  const sortedTemplates = useMemo(
    () =>
      matchingTemplates
        .map((template, originalIndex) => ({ template, originalIndex }))
        .sort((left, right) => {
          const comparison =
            sortKey === "updatedAt"
              ? compareActivityTemplateUpdatedAt(
                  left.template,
                  right.template,
                  sortDirection,
                )
              : compareActivityTemplateValues(
                  activityTemplateSortValue(left.template, sortKey),
                  activityTemplateSortValue(right.template, sortKey),
                  sortDirection,
                );
          return comparison || left.originalIndex - right.originalIndex;
        })
        .map(({ template }) => template),
    [matchingTemplates, sortDirection, sortKey],
  );

  const updateSort = (nextSortKey: ActivityTemplateSortKey): void => {
    const nextDirection =
      nextSortKey === sortKey && sortDirection === "ascending"
        ? "descending"
        : "ascending";
    setOpenActivityStepsId(null);
    updateListSearch((nextSearch) => {
      nextSearch.set("sort", nextSortKey);
      nextSearch.set(
        "direction",
        nextDirection === "descending" ? "desc" : "asc",
      );
    }, "push");
  };

  const sortProps = (key: ActivityTemplateSortKey) => ({
    sortDirection: sortKey === key ? sortDirection : ("none" as const),
    onSort: () => updateSort(key),
  });

  useEffect(() => {
    if (
      openActivityStepsId &&
      !matchingTemplates.some(
        (template) => template.id === openActivityStepsId,
      )
    ) {
      setOpenActivityStepsId(null);
    }
  }, [matchingTemplates, openActivityStepsId]);
  const totalTemplates = templates.length;
  const resultCount =
    statusFilter === "all" && !search.trim() && !activityTypeFilter
      ? `${totalTemplates} ${totalTemplates === 1 ? "template" : "templates"}`
      : `${matchingTemplates.length} of ${totalTemplates} ${
          totalTemplates === 1 ? "template" : "templates"
        }`;

  const columns: readonly PortalTableColumn<PortalActivityTemplateSummary>[] = [
    {
      key: "actions",
      header: <ActionsColumnHeader />,
      headerLabel: "Actions",
      initialWidth: 60,
      minWidth: 60,
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (template) => (
        <RowActionMenu
          clinicianCode={clinicianCode}
          editPath={withActivityTemplatesReturnTo(
            activityTemplateEditPath(template.id),
            listReturnTo,
          )}
          activityName={template.name}
          canEdit
          showDuplicate={false}
          showDelete={false}
        />
      ),
    },
    {
      key: "name",
      header: "Activity Template",
      initialWidth: 300,
      minWidth: 220,
      ...sortProps("name"),
      render: (template) => (
        <span className="cp-homework-name">
          {template.name || "Untitled template"}
        </span>
      ),
    },
    {
      key: "activityType",
      header: "Activity Type",
      initialWidth: 240,
      minWidth: 160,
      ...sortProps("activityType"),
      render: (template) => (
        <ActivityTypePill activityType={template.activityType} />
      ),
    },
    {
      key: "status",
      header: "Status",
      initialWidth: 105,
      minWidth: 105,
      ...sortProps("status"),
      render: (template) => (
        <ActivityTemplateStatusPill status={template.status} />
      ),
    },
    {
      key: "editSteps",
      header: "Edit Steps",
      initialWidth: 135,
      minWidth: 120,
      resizable: false,
      render: (template) => (
        <a
          className="cp-edit-steps-button"
          href={templateStepsEditorPath({
            clinicianCode,
            templateId: template.id,
            returnUrl: window.location.href,
          })}
        >
          Edit Steps
        </a>
      ),
    },
    {
      key: "activitySteps",
      header: "Activity Steps",
      initialWidth: 440,
      minWidth: 280,
      ...sortProps("activitySteps"),
      cellClassName: "cp-table-cell--multiline",
      render: (template) =>
        template.activitySteps.trim() ? (
          <ActivityStepsPreview
            activityName={template.name}
            activitySteps={template.activitySteps}
            isOpen={openActivityStepsId === template.id}
            onOpenChange={(open) =>
              setOpenActivityStepsId(open ? template.id : null)
            }
          />
        ) : (
          <span className="cp-empty-value">–</span>
        ),
    },
    {
      key: "updatedAt",
      header: "Modified",
      initialWidth: 185,
      minWidth: 165,
      ...sortProps("updatedAt"),
      cellClassName: "cp-table-cell--date-time",
      render: (template) => formatLocalDateTime(template.updatedAt),
    },
  ];

  return (
    <ClinicianPortalLayout
      activeGlobalKey="templates"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Activity Templates"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
      />
      <div className="cp-page-content cp-page-content--wide cp-page-content--sticky-list cp-page-content--compact-footer">
        {state.status === "loading" ? (
          <LoadingState label="Loading activity templates" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState
            error={new Error("Activity templates are not available")}
          />
        ) : null}
        {available ? (
          <section aria-label="Activity Templates list">
            <div className="cp-content-header">
              <div>
                <p>Browse reusable activities for assigning homework.</p>
              </div>
              <strong className="cp-record-count">{resultCount}</strong>
            </div>
            <div
              className="cp-history-filters"
              role="group"
              aria-label="Activity template status filter"
            >
              {statusFilterOptions.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  aria-pressed={statusFilter === option.value}
                  onClick={() => {
                    if (option.value === statusFilter) return;
                    setOpenActivityStepsId(null);
                    updateListSearch(
                      (nextSearch) => nextSearch.set("status", option.value),
                      "push",
                    );
                  }}
                >
                  {option.label} ({statusCounts[option.value]})
                </button>
              ))}
            </div>
            <div className="cp-list-toolbar">
              <div className="cp-list-toolbar__filters">
                <label className="cp-search-control">
                  <Search aria-hidden="true" />
                  <span className="cp-visually-hidden">Search templates</span>
                  <input
                    type="search"
                    value={search}
                    placeholder="Search templates"
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setOpenActivityStepsId(null);
                      updateListSearch((nextSearch) => {
                        if (nextValue) nextSearch.set("q", nextValue);
                        else nextSearch.delete("q");
                      }, "replace");
                    }}
                  />
                </label>
                <div className="cp-activity-type-filter">
                  <SingleSelectPillField
                    id="activity-template-type-filter"
                    value={activityTypeFilter}
                    options={activityTypeOptions}
                    onChange={(activityType) => {
                      setOpenActivityStepsId(null);
                      updateListSearch((nextSearch) => {
                        if (activityType) {
                          nextSearch.set("activityType", activityType);
                        } else {
                          nextSearch.delete("activityType");
                        }
                      }, "push");
                    }}
                    ariaLabel="Filter by Activity Type"
                    placeholder="All Activity Types"
                    searchPlaceholder="Search Activity Types"
                    emptyMessage="No Activity Types found"
                    resolveColor={(option) =>
                      option.value
                        ? getActivityTypePillToken(option.label)
                        : "neutral"
                    }
                  />
                </div>
              </div>
              <div className="cp-list-toolbar__actions">
                <button
                  type="button"
                  className="cp-button cp-button--secondary"
                  disabled={state.status === "refreshing"}
                  onClick={refresh}
                >
                  <RefreshCw aria-hidden="true" />
                  {state.status === "refreshing" ? "Refreshing…" : "Refresh"}
                </button>
                <PortalLink
                  className="cp-button cp-button--secondary"
                  path={activityTemplateOverviewPath}
                  clinicianCode={clinicianCode}
                >
                  <BarChart3 aria-hidden="true" />
                  Template Overview
                </PortalLink>
                <PortalLink
                  className="cp-button cp-button--primary"
                  path={addActivityTemplatePath}
                  clinicianCode={clinicianCode}
                >
                  <Plus aria-hidden="true" />
                  Add Template
                </PortalLink>
              </div>
            </div>
            {matchingTemplates.length ? (
              <PortalDataTable
                caption="Activity Templates"
                columns={columns}
                records={sortedTemplates}
                getRecordKey={(template) => template.id}
                getRecordOpenLabel={(template) =>
                  `Open ${template.name || "activity template"}`
                }
                onRecordOpen={(template) =>
                  navigateWithinPortal(
                    withClinicianCode(
                      withActivityTemplatesReturnTo(
                        activityTemplateDetailPath(template.id),
                        listReturnTo,
                      ),
                      clinicianCode,
                    ),
                  )
                }
              />
            ) : (
              <div className="cp-homework-table__empty">
                <h3>No matching templates</h3>
                <p>
                  {templates.length
                    ? "Try a different search, status, or Activity Type filter."
                    : "No templates are available."}
                </p>
              </div>
            )}
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
